/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-cm-setup/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import { boolToYesNo, getStudySummaryDisplay } from '@components/admin/studies/study-cm-setup/study-cm-setup.helper';
import OutlinedWrapper from '@components/core/outlined-wrapper';
import { useTranslation } from '@hooks/index';
import { StudyFields } from '@queries/generated/fragments.generated';

import '@components/admin/studies/admin-study.scss';

const StudyCMSetup = ({ study, localState }: { study: StudyFields; localState: any }) => {
    const { t } = useTranslation();

    const value = localState?.fields?.find((item: any) => item.key === study.volumeAdjustedScoringNumberField);

    return (
        <div className="study-settings-wrapper">
            <div className="d-flex">
                <div className="data-wrap">
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:siteStatusToExclude')}:</strong>
                        </span>
                        <span className="value">
                            {study?.sitesStatusesToExclude?.length ? study.sitesStatusesToExclude.join(', ') : ''}
                        </span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:siteScoreGreenThreshold')}:</strong>
                        </span>
                        <span className="value">{study.scoreGreenThreshold}</span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:siteScoreAmberThreshold')}:</strong>
                        </span>
                        <span className="value">{study.scoreAmberThreshold}</span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:volumeAdjustedScoring')}:</strong>
                        </span>
                        <span className="value">{t(boolToYesNo(study.volumeAdjustedScoring))}</span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:subjectPopulation')}:</strong>
                        </span>
                        <span className="value">{value?.name ?? ''}</span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:siteScoreThreshold')}:</strong>
                        </span>
                        <span className="value">{study.scoreThreshold ?? 0}</span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:adjustmentWeight')}:</strong>
                        </span>
                        <span className="value">{study.adjustmentWeight ?? 0}</span>
                    </div>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:importFileName')}:</strong>
                        </span>
                        <span className="value">{study.importDefinitionsKey ?? ''}</span>
                    </div>
                </div>
                <div className="data-wrap">
                    <OutlinedWrapper customClass="cm-setup-tr" label={t('admin:transformationRules')}>
                        <div className="row">
                            <span className="title">
                                <strong>{t('admin:svtAssociation')}:</strong>
                            </span>
                            <span className="value to-capitalize">{t(`admin:${study.svtDependency ?? ''}`)}</span>
                        </div>
                        <div className="row">
                            <span className="title">
                                <strong>{t('admin:assessmentAssociation')}:</strong>
                            </span>
                            <span className="value">{t(boolToYesNo(study.assessmentAssociation))}</span>
                        </div>
                        <div className="row">
                            <span className="title">
                                <strong>{t('admin:useInformedConsentedDate')}:</strong>
                            </span>
                            <span className="value">{t(boolToYesNo(study.useInformedConsentedDate))}</span>
                        </div>
                        <div className="row">
                            <span className="title">
                                <strong>{t('admin:calculateVisits')}:</strong>
                            </span>
                            <span className="value">{t(boolToYesNo(study.calculateVisits))}</span>
                        </div>
                        <div className="row">
                            <span className="title">
                                <strong>{t('admin:calculateCrfs')}:</strong>
                            </span>
                            <span className="value">{t(boolToYesNo(study.calculateCrfs))}</span>
                        </div>
                        <div className="row">
                            <span className="title">
                                <strong>{t('admin:calculateAssessments')}:</strong>
                            </span>
                            <span className="value">{t(boolToYesNo(study.calculateAssessments))}</span>
                        </div>
                    </OutlinedWrapper>
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:displayOnStudySummary')}:</strong>
                        </span>
                        <span className="value">{getStudySummaryDisplay(study, t)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudyCMSetup;
